import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import * as UIcons from '@iconscout/react-unicons'
import useSWR, { mutate } from 'swr'
import dayjs from 'dayjs'
import Btn from 'components/Btn'
import { apiRequest } from 'store/Misc/apiRequest'
import { destructureErrorMessage } from 'Helper'
import PageHeader from '../shared/PageHeader'
import ConfirmDialog from '../shared/ConfirmDialog'
import RolesStats from './components/RolesStats'
import RoleFilterBar from './components/RoleFilterBar'
import RolesTable from './components/RolesTable'
import RoleDetail from './components/RoleDetail'
import AssignedPermissionsView from './components/AssignedPermissionsView'
import RoleWizard from './components/RoleWizard'

export default withTranslation()(function RoleManagement({ t }) {
  const { data, isValidating } = useSWR('user-mgmt/roles')

  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(dayjs())

  const [viewingRoleId, setViewingRoleId] = useState(null)
  const [editingRole, setEditingRole] = useState(null)
  const [duplicatingRole, setDuplicatingRole] = useState(null)
  const [permissionsTarget, setPermissionsTarget] = useState(null)
  const [toggleTarget, setToggleTarget] = useState(null)
  const [busy, setBusy] = useState(false)

  const roles = data?.data ?? []
  const viewingRole = roles.find((role) => role.id === viewingRoleId)

  useEffect(() => {
    if (!isValidating) setLastUpdated(dayjs())
  }, [isValidating])

  const isActive = (role) => (role.status ?? 'active') !== 'inactive'

  const refresh = () => mutate('user-mgmt/roles')

  const confirmToggleStatus = async () => {
    const willDisable = isActive(toggleTarget)
    setBusy(true)
    try {
      await apiRequest({
        method: 'put',
        url: `user-mgmt/roles/${toggleTarget.id}`,
        payload: { status: willDisable ? 'inactive' : 'active' }
      })
      mutate('user-mgmt/roles')
      toast.success(t(`settings.role_management.success_${willDisable ? 'disable' : 'enable'}`))
      setToggleTarget(null)
    } catch ({ message }) {
      destructureErrorMessage(message).map((msg) => toast.error(msg, { autoClose: 15000 }))
    } finally {
      setBusy(false)
    }
  }

  const duplicateRole = (role) => setDuplicatingRole({
    ...role,
    id: undefined,
    name: undefined,
    display_name: `${role.display_name ?? role.name} (Copy)`
  })

  return (
    <>
      {
        permissionsTarget ? (
          <AssignedPermissionsView
            role={permissionsTarget}
            onBack={() => setPermissionsTarget(null)}
            onEdit={() => setEditingRole(permissionsTarget)}
            onDuplicate={() => duplicateRole(permissionsTarget)}
            onToggleStatus={() => setToggleTarget(permissionsTarget)}
          />
        ) : viewingRole ? (
          <RoleDetail
            role={viewingRole}
            onEdit={() => setEditingRole(viewingRole)}
            onDuplicate={() => duplicateRole(viewingRole)}
            onToggleStatus={() => setToggleTarget(viewingRole)}
            onViewPermissions={() => setPermissionsTarget(viewingRole)}
          />
        ) : (
          <section className="w-full">
            <PageHeader
              crumbs={[
                { name: t('settings.hub.title'), url: '/settings' },
                { name: t('settings.hub.team.title'), url: '/settings/team-access' },
                { name: t('settings.role_management.page_title') }
              ]}
              title={t('settings.role_management.page_title')}
              right={
                <div className="flex items-center gap-2">
                  <span className="text-sm text-secondary whitespace-nowrap">
                    {t('settings.role_management.last_update', { date: lastUpdated.format('D MMM YY') })}
                  </span>

                  <button
                    type="button"
                    onClick={refresh}
                    className="w-9 h-9 rounded-full border border-border-lines flex items-center justify-center hover:bg-background shrink-0"
                  >
                    <UIcons.UilRedo className="text-secondary" size={16} />
                  </button>

                  <Btn
                    content={() => <span className="flex items-center gap-1.5"><UIcons.UilPlus size={16} /> {t('settings.role_management.add')}</span>}
                    className="text-white bg-primary rounded-full h-9 px-4 font-medium"
                    click={() => setShowAdd(true)}
                  />
                </div>
              }
            />

            <RolesStats />

            <RoleFilterBar
              q={q}
              onChangeQ={setQ}
              status={status}
              onChangeStatus={setStatus}
            />

            <RolesTable
              q={q}
              status={status}
              onViewDetail={(role) => setViewingRoleId(role.id)}
              onEdit={setEditingRole}
              onDuplicate={duplicateRole}
              onToggleStatus={setToggleTarget}
            />
          </section>
        )
      }

      {
        showAdd && (
          <RoleWizard
            handleClick={() => setShowAdd(false)}
            onSaved={() => mutate('user-mgmt/roles')}
          />
        )
      }

      {
        editingRole && (
          <RoleWizard
            role={editingRole}
            handleClick={() => setEditingRole(null)}
            onSaved={() => mutate('user-mgmt/roles')}
          />
        )
      }

      {
        duplicatingRole && (
          <RoleWizard
            role={duplicatingRole}
            handleClick={() => setDuplicatingRole(null)}
            onSaved={() => mutate('user-mgmt/roles')}
          />
        )
      }

      {
        toggleTarget && (
          <ConfirmDialog
            title={t(isActive(toggleTarget) ? 'settings.role_management.deactivate' : 'settings.role_management.enable')}
            description={t(isActive(toggleTarget) ? 'settings.role_management.deactivate_confirm' : 'settings.role_management.enable_confirm')}
            confirmText={t(isActive(toggleTarget) ? 'settings.role_management.deactivate' : 'settings.role_management.enable')}
            danger={isActive(toggleTarget)}
            loading={busy}
            onConfirm={confirmToggleStatus}
            onCancel={() => setToggleTarget(null)}
          />
        )
      }
    </>
  )
})
